/**
 * The frames of the capture being built, held outside React.
 *
 * An operator photographs one item from several angles: the part, its
 * label, its rating plate. Those frames are one capture, and the extraction
 * pipeline reads every one of them
 * (`apps/capture-api/src/extraction/textract-deterministic-reader.ts`). So
 * the viewfinder collects frames before it queues anything, and a frame the
 * quality gate wants to stop goes to CA-04 for the operator's decision and
 * comes back into the same set.
 *
 * The set cannot live in the viewfinder's own state, because CA-04 is a
 * route and navigating there unmounts it. It cannot travel in navigation
 * state either: that is structured-cloned into session history, where
 * multi-megabyte Blobs are the wrong thing to put, and an earlier build
 * worked around exactly that by queueing `new Blob()`, which made every
 * gate-failing capture unuploadable.
 *
 * A module-scoped holder is what carries them. The lifetime is one capture,
 * in one tab, and a reload deliberately loses it: bytes that are gone are
 * gone, and the screens send the operator back to the viewfinder rather
 * than queueing something empty.
 */

import type { CaptureMode, QualityGateResult } from './api/types';
import type { QualityAdvice } from './quality-advice';

export interface CaptureFrame {
  fingerprint: string;
  contentType: string;
  bytes: Blob;
  qualityResult: QualityGateResult;
  advice: QualityAdvice;
  manualFields: Record<string, string>;
}

/**
 * How many frames one capture may carry. Matches
 * `MAX_ASSETS_PER_CAPTURE` in `apps/capture-api/src/ingestion/create.ts`,
 * which refuses a fourth: the server pays one Textract call and one model
 * call per frame, so the cap is a cost bound, not a UI preference.
 */
export const MAX_FRAMES = 3;

let frames: CaptureFrame[] = [];
let underDecision: { frame: CaptureFrame; mode: CaptureMode } | null = null;

/**
 * What became of a frame offered to the capture. The three outcomes read
 * differently to the operator, so they are told apart here rather than
 * collapsed into a boolean the screen cannot explain.
 */
export type AddFrameOutcome = 'added' | 'full' | 'duplicate';

/** The frames collected so far, in capture order. */
export function listFrames(): CaptureFrame[] {
  return frames;
}

/**
 * Adds a frame to the capture.
 *
 * The same photograph twice is refused. Two frames sharing a fingerprint
 * share an asset key, so the server would store one object, pay Textract and
 * the model for it twice and read the same text twice; on the screen the two
 * would share a thumbnail and a remove control, and removing either would
 * take both. A duplicate arrives from the file picker, where choosing the
 * same file again is deliberately allowed so a retry after a decode failure
 * still fires.
 */
export function addFrame(frame: CaptureFrame): AddFrameOutcome {
  if (frames.some((held) => held.fingerprint === frame.fingerprint)) return 'duplicate';
  if (frames.length >= MAX_FRAMES) return 'full';
  frames = [...frames, frame];
  return 'added';
}

/** Drops the frame with this fingerprint. One frame holds it, by the rule above. */
export function removeFrame(fingerprint: string): void {
  frames = frames.filter((frame) => frame.fingerprint !== fingerprint);
}

/**
 * The job the held frames were collected for. The set outlives the
 * viewfinder (CA-04 is a route), and it may never follow the operator into
 * a different job.
 */
let framesForJob: string | null = null;

/**
 * Claims the holder for a job before the viewfinder reads it. Everything
 * held for another job is dropped first. Returns true when the holder
 * changed hands, which tells the caller to drop its own held work too.
 */
export function claimFramesFor(jobId: string): boolean {
  if (framesForJob === jobId) return false;
  clearFrames();
  framesForJob = jobId;
  return true;
}

/** Drops every frame. Called once the capture is queued, and on a discard. */
export function clearFrames(): void {
  frames = [];
  underDecision = null;
}

/** Holds a frame for CA-04. The operator decides before it joins the set. */
export function holdForDecision(frame: CaptureFrame, mode: CaptureMode): void {
  underDecision = { frame, mode };
}

export function readDecision(): { frame: CaptureFrame; mode: CaptureMode } | null {
  return underDecision;
}

/**
 * The operator kept the frame the gate wanted to stop. It joins the set and
 * queues with the rest, carrying its failing `QualityGateResult`, which is
 * what SC3 note 3 requires: the gate advises and the server decides.
 *
 * The outcome travels back to the viewfinder, because a capture that was
 * already full or already holds this photograph does not take the frame, and
 * an operator told "kept" about a frame that was dropped walks away from the
 * item believing they have it.
 */
export function keepDecidedFrame(): AddFrameOutcome | 'nothing-held' {
  if (!underDecision) return 'nothing-held';
  const outcome = addFrame(underDecision.frame);
  underDecision = null;
  return outcome;
}

/** The operator chose to shoot it again. The frame is dropped. */
export function discardDecision(): void {
  underDecision = null;
}

/**
 * The shot list a job resolves to when the job could not be read, offline
 * or before the answer arrives. The same three labels the item standard and
 * the API fall back to (plan/portal-final/INTERFACES.md section 5).
 */
export const DEFAULT_SHOT_LIST: readonly string[] = ['Whole item', 'Rating plate', 'Label'];

/** One slot on the viewfinder's frame box. */
export interface ShotSlot {
  /** The shot list's label, or `Frame n` for a frame past the list's end. */
  label: string;
  /** The frame filling this slot, absent while the slot waits for one. */
  frame?: CaptureFrame;
}

/**
 * The slots the viewfinder draws: one per shot list label, filled left to
 * right in the order the frames were taken. The list guides and never
 * blocks, so a job with fewer labels than frames still shows every frame,
 * named by its position, and the slot count never passes `MAX_FRAMES`.
 */
export function shotSlots(labels: readonly string[], held: readonly CaptureFrame[]): ShotSlot[] {
  const count = Math.min(MAX_FRAMES, Math.max(labels.length, held.length));
  return Array.from({ length: count }, (_, index) => ({
    label: labels[index] ?? `Frame ${index + 1}`,
    ...(held[index] ? { frame: held[index] } : {}),
  }));
}
